import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { selectIsInternalAdmin, selectIsCsmUser } from '../../accessors'
import llmPlatformApi from '../../services/api/llmPlatform'
import styles from './LLMPlatform.module.scss'

const LLMPlatform = () => {
  // All hooks must be called at the top level, before any conditional returns
  const isInternalAdmin = useSelector(selectIsInternalAdmin)
  const isCsmUser = useSelector(selectIsCsmUser)
  const currentHotelId = useSelector(s => s.login.currentHotelId)
  const subsidiaryList = useSelector(s => s.login.subsidiaryList)
  
  // Local state for selected hotel (to avoid full app reload)
  const [selectedHotelId, setSelectedHotelId] = useState(currentHotelId || null)
  
  const [activeTab, setActiveTab] = useState('chat')
  const [loading, setLoading] = useState(false)
  const [response, setResponse] = useState(null)
  const [error, setError] = useState(null)
  
  // Chat state
  const [chatMessage, setChatMessage] = useState('')
  const [conversationId, setConversationId] = useState(null)
  
  // Campaign state
  const [campaignGoal, setCampaignGoal] = useState('')
  const [campaignChannel, setCampaignChannel] = useState('SMS')
  const [campaignTone, setCampaignTone] = useState('friendly')
  const [variantCount, setVariantCount] = useState(3)
  
  // CSM state
  const [csmQuestion, setCsmQuestion] = useState('')
  
  // Forecast state
  const [monthsAhead, setMonthsAhead] = useState(3)
  
  // Recommendations state
  const [timeWindow, setTimeWindow] = useState('2026-02')
  
  // Intent state
  const [intentMessage, setIntentMessage] = useState('')
  
  // Sync local selected hotel with Redux currentHotelId when it changes
  useEffect(() => {
    if (currentHotelId && currentHotelId !== selectedHotelId) {
      setSelectedHotelId(currentHotelId)
    }
  }, [currentHotelId])
  
  // Derived values (computed after hooks)
  // Use selectedHotelId instead of currentHotelId to avoid triggering full app reload
  // Note: subsidiaryList uses 'subsidiaryId' not 'id'
  const currentSubsidiary = subsidiaryList?.find(s => s.subsidiaryId === selectedHotelId || s.id === selectedHotelId)
  
  // Check access - only admins and CSMs (after all hooks)
  if (!isInternalAdmin && !isCsmUser) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          Access denied. This page is only available to Directful admins and CSMs.
        </div>
      </div>
    )
  }
  
  const handleChatSubmit = async () => {
    if (!chatMessage.trim() || !selectedHotelId) return
    
    setLoading(true)
    setError(null)
    setResponse(null)
    try {
      const result = await llmPlatformApi.chatRespond(
        selectedHotelId,
        null, // propertyId (optional)
        chatMessage,
        conversationId
      )
      setResponse(result)
      if (result?.conversation_id || result?.conversationId) {
        setConversationId(result.conversation_id || result.conversationId)
      }
    } catch (err) {
      setError(err.message || 'Failed to get response')
      console.error('Chat error:', err)
    } finally {
      setLoading(false)
    }
  }
  
  const handleCampaignGenerate = async () => {
    if (!campaignGoal.trim() || !selectedHotelId) return
    
    setLoading(true)
    setError(null)
    setResponse(null)
    try {
      const result = await llmPlatformApi.generateCampaign(
        selectedHotelId,
        null,
        campaignGoal,
        campaignChannel,
        campaignTone,
        variantCount
      )
      setResponse(result)
    } catch (err) {
      setError(err.message || 'Failed to generate campaign')
      console.error('Campaign error:', err)
    } finally {
      setLoading(false)
    }
  }
  
  const handleCSMQuestion = async () => {
    if (!csmQuestion.trim() || !selectedHotelId) return
    
    setLoading(true)
    setError(null)
    setResponse(null)
    try {
      const result = await llmPlatformApi.csmAnswer(
        selectedHotelId,
        null,
        csmQuestion
      )
      setResponse(result)
    } catch (err) {
      setError(err.message || 'Failed to get answer')
      console.error('CSM error:', err)
    } finally {
      setLoading(false)
    }
  }
  
  const handleForecast = async () => {
    if (!selectedHotelId) return
    
    setLoading(true)
    setError(null)
    setResponse(null)
    try {
      const result = await llmPlatformApi.getForecast(
        selectedHotelId,
        null,
        monthsAhead
      )
      setResponse(result)
    } catch (err) {
      setError(err.message || 'Failed to get forecast')
      console.error('Forecast error:', err)
    } finally {
      setLoading(false)
    }
  }
  
  const handleRecommendations = async () => {
    if (!selectedHotelId) return
    
    setLoading(true)
    setError(null)
    setResponse(null)
    try {
      const result = await llmPlatformApi.getCampaignRecommendations(
        selectedHotelId,
        null,
        timeWindow
      )
      setResponse(result)
    } catch (err) {
      setError(err.message || 'Failed to get recommendations')
      console.error('Recommendations error:', err)
    } finally {
      setLoading(false)
    }
  }
  
  const handleIntentClassify = async () => {
    if (!intentMessage.trim()) return
    
    setLoading(true)
    setError(null)
    setResponse(null)
    try {
      const result = await llmPlatformApi.classifyIntent(intentMessage)
      setResponse(result)
    } catch (err) {
      setError(err.message || 'Failed to classify intent')
      console.error('Intent error:', err)
    } finally {
      setLoading(false)
    }
  }
  
  return (
    <div className={styles.container}>
      <h1>LLM Platform - AI Assistant</h1>
      <p className={styles.subtitle}>
        Access to LLM Platform services for Directful admins and CSMs
      </p>
      
      {!selectedHotelId && subsidiaryList && subsidiaryList.length > 0 && (
        <div className={styles.warning}>
          <p>Please select a hotel/subsidiary to use LLM Platform features.</p>
          <select
            className={styles.hotelSelector}
            value={selectedHotelId || ''}
            onChange={(e) => {
              // Only update local state - don't trigger full app reload
              if (e.target.value) {
                setSelectedHotelId(e.target.value)
              }
            }}
          >
            <option value="">Select a hotel...</option>
            {subsidiaryList.map(subsidiary => {
              // Use subsidiaryId if available, fallback to id
              const hotelId = subsidiary.subsidiaryId || subsidiary.id
              const hotelName = subsidiary.subsidiaryName || subsidiary.name || subsidiary.shortName || `Hotel ${hotelId}`
              return (
                <option key={hotelId} value={hotelId}>
                  {hotelName}
                </option>
              )
            })}
          </select>
        </div>
      )}
      
      {/* Show hotel selector even when a hotel is selected, to allow switching */}
      {selectedHotelId && subsidiaryList && subsidiaryList.length > 1 && (
        <div className={styles.hotelSelectorContainer}>
          <label className={styles.hotelSelectorLabel}>Selected Hotel:</label>
          <select
            className={styles.hotelSelector}
            value={selectedHotelId || ''}
            onChange={(e) => {
              // Only update local state - don't trigger full app reload
              if (e.target.value) {
                setSelectedHotelId(e.target.value)
              }
            }}
          >
            {subsidiaryList.map(subsidiary => {
              // Use subsidiaryId if available, fallback to id
              const hotelId = subsidiary.subsidiaryId || subsidiary.id
              const hotelName = subsidiary.subsidiaryName || subsidiary.name || subsidiary.shortName || `Hotel ${hotelId}`
              return (
                <option key={hotelId} value={hotelId}>
                  {hotelName}
                </option>
              )
            })}
          </select>
        </div>
      )}
      
      {!selectedHotelId && (!subsidiaryList || subsidiaryList.length === 0) && (
        <div className={styles.warning}>
          No hotels available. Please contact support if you believe this is an error.
        </div>
      )}
      
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'chat' ? styles.active : ''}`}
          onClick={() => {
            setActiveTab('chat')
            setResponse(null)
            setError(null)
          }}
        >
          💬 Chat
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'campaign' ? styles.active : ''}`}
          onClick={() => {
            setActiveTab('campaign')
            setResponse(null)
            setError(null)
          }}
        >
          📢 Campaign
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'csm' ? styles.active : ''}`}
          onClick={() => {
            setActiveTab('csm')
            setResponse(null)
            setError(null)
          }}
        >
          👔 CSM Copilot
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'forecast' ? styles.active : ''}`}
          onClick={() => {
            setActiveTab('forecast')
            setResponse(null)
            setError(null)
          }}
        >
          📊 Forecast
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'recommend' ? styles.active : ''}`}
          onClick={() => {
            setActiveTab('recommend')
            setResponse(null)
            setError(null)
          }}
        >
          🎯 Recommendations
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'intent' ? styles.active : ''}`}
          onClick={() => {
            setActiveTab('intent')
            setResponse(null)
            setError(null)
          }}
        >
          🧠 Intent
        </button>
      </div>
      
      <div className={styles.content}>
        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <div className={styles.tabContent}>
            {!selectedHotelId && (
              <div className={styles.warning}>
                Please select a hotel from the dropdown above to use this feature.
              </div>
            )}
            <div className={styles.formGroup}>
              <label>User Message:</label>
              <textarea
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                placeholder="Ask a question..."
                disabled={loading || !selectedHotelId}
                rows={4}
              />
            </div>
            {conversationId && (
              <div className={styles.info}>
                Conversation ID: {conversationId}
              </div>
            )}
            <button
              className={styles.btn}
              onClick={handleChatSubmit}
              disabled={loading || !chatMessage.trim() || !selectedHotelId}
            >
              {loading ? 'Sending...' : 'Send Message'}
            </button>
          </div>
        )}
        
        {/* Campaign Tab */}
        {activeTab === 'campaign' && (
          <div className={styles.tabContent}>
            {!selectedHotelId && (
              <div className={styles.warning}>
                Please select a hotel from the dropdown above to use this feature.
              </div>
            )}
            <div className={styles.formGroup}>
              <label>Campaign Goal:</label>
              <input
                type="text"
                value={campaignGoal}
                onChange={(e) => setCampaignGoal(e.target.value)}
                placeholder="e.g., Increase bookings for next month"
                disabled={loading || !selectedHotelId}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Channel:</label>
              <select
                value={campaignChannel}
                onChange={(e) => setCampaignChannel(e.target.value)}
                disabled={loading || !selectedHotelId}
              >
                <option value="SMS">SMS</option>
                <option value="MMS">MMS</option>
                <option value="email">Email</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>Tone:</label>
              <select
                value={campaignTone}
                onChange={(e) => setCampaignTone(e.target.value)}
                disabled={loading || !selectedHotelId}
              >
                <option value="friendly">Friendly</option>
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>Number of Variants:</label>
              <input
                type="number"
                value={variantCount}
                onChange={(e) => setVariantCount(parseInt(e.target.value) || 3)}
                min="1"
                max="10"
                disabled={loading || !selectedHotelId}
              />
            </div>
            <button
              className={styles.btn}
              onClick={handleCampaignGenerate}
              disabled={loading || !campaignGoal.trim() || !selectedHotelId}
            >
              {loading ? 'Generating...' : 'Generate Campaign'}
            </button>
          </div>
        )}
        
        {/* CSM Tab */}
        {activeTab === 'csm' && (
          <div className={styles.tabContent}>
            {!selectedHotelId && (
              <div className={styles.warning}>
                Please select a hotel from the dropdown above to use this feature.
              </div>
            )}
            <div className={styles.formGroup}>
              <label>Question:</label>
              <textarea
                value={csmQuestion}
                onChange={(e) => setCsmQuestion(e.target.value)}
                placeholder="Ask a question about the property..."
                disabled={loading || !selectedHotelId}
                rows={4}
              />
            </div>
            <button
              className={styles.btn}
              onClick={handleCSMQuestion}
              disabled={loading || !csmQuestion.trim() || !selectedHotelId}
            >
              {loading ? 'Asking...' : 'Ask Question'}
            </button>
          </div>
        )}
        
        {/* Forecast Tab */}
        {activeTab === 'forecast' && (
          <div className={styles.tabContent}>
            {!selectedHotelId && (
              <div className={styles.warning}>
                Please select a hotel from the dropdown above to use this feature.
              </div>
            )}
            <div className={styles.formGroup}>
              <label>Months Ahead:</label>
              <input
                type="number"
                value={monthsAhead}
                onChange={(e) => setMonthsAhead(parseInt(e.target.value) || 3)}
                min="1"
                max="12"
                disabled={loading || !selectedHotelId}
              />
            </div>
            <button
              className={styles.btn}
              onClick={handleForecast}
              disabled={loading || !currentSubsidiary}
            >
              {loading ? 'Loading...' : 'Get Forecast'}
            </button>
          </div>
        )}
        
        {/* Recommendations Tab */}
        {activeTab === 'recommend' && (
          <div className={styles.tabContent}>
            {!selectedHotelId && (
              <div className={styles.warning}>
                Please select a hotel from the dropdown above to use this feature.
              </div>
            )}
            <div className={styles.formGroup}>
              <label>Time Window (YYYY-MM):</label>
              <input
                type="text"
                value={timeWindow}
                onChange={(e) => setTimeWindow(e.target.value)}
                placeholder="2026-02"
                disabled={loading || !selectedHotelId}
              />
            </div>
            <button
              className={styles.btn}
              onClick={handleRecommendations}
              disabled={loading || !currentSubsidiary}
            >
              {loading ? 'Loading...' : 'Get Recommendations'}
            </button>
          </div>
        )}
        
        {/* Intent Tab */}
        {activeTab === 'intent' && (
          <div className={styles.tabContent}>
            <div className={styles.formGroup}>
              <label>Message to Classify:</label>
              <textarea
                value={intentMessage}
                onChange={(e) => setIntentMessage(e.target.value)}
                placeholder="e.g., I want to cancel my booking"
                disabled={loading}
                rows={4}
              />
            </div>
            <button
              className={styles.btn}
              onClick={handleIntentClassify}
              disabled={loading || !intentMessage.trim()}
            >
              {loading ? 'Classifying...' : 'Classify Intent'}
            </button>
          </div>
        )}
      </div>
      
      {/* Response Display */}
      {(response || error) && (
        <div className={`${styles.response} ${error ? styles.error : styles.success}`}>
          <h3>{error ? '❌ Error' : '✅ Response'}</h3>
          <pre className={styles.responseContent}>
            {JSON.stringify(error || response, null, 2)}
          </pre>
        </div>
      )}
    </div>
  )
}

export default LLMPlatform
